<template>
  <component :is="surroundingComponent" :disabled="endOfLife">
    <div class="notification" :class="classes" :role="role" :aria-busy="busy" :aria-labelledby="headingId" :aria-modal="lock" aria-live="polite">
      <VueGlobalEvents v-if="closable || lock" @keyup.esc="$emit('close')" />
      <button v-if="closable" type="button" class="close" @click="$emit('close')">
        <VSvgIcon name="cancel" variant="outlined" />
        <span class="sr-only">{{ $t('common.close') }}</span>
      </button>
      <div class="inner" :class="{ 'text-center': center }">
        <h3 v-if="headingComputed" :id="headingId" class="notification-heading" :class="{ 'sr-only': !!icon }">{{ $to(headingComputed) }}</h3>
        <div v-if="icon" class="notification-left" aria-hidden="true">
          <div class="icon-frame">
            <VSvgIcon :name="statusSymbol(type, 'error')" variant="outlined" size="large" />
          </div>
        </div>
        <div class="notification-content">
          <slot><span v-html="contentComputed" /></slot>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import VSvgIcon from '@/components/UI/VSvgIcon.vue';
import { statusSymbol } from '@/utils/statusSymbol';

export default {
  name: 'NotificationBox',

  components: {
    VSvgIcon,
    VueGlobalEvents: () => import('vue-global-events'),
  },

  props: {
    type: {
      type: String,
      required: true,
      validator(value) {
        return ['SUCCESS', 'WARNING', 'ERROR', 'INFO', 'PROMPT', 'LOADING'].includes(value);
      },
    },

    content: {
      type: String,
      required: false,
      default: null,
    },

    supplier: {
      type: Function,
      required: false,
      default: null,
    },

    closable: {
      type: Boolean,
      required: false,
    },

    heading: {
      type: String,
      required: false,
      default: null,
    },

    icon: {
      type: String,
      required: false,
      default: null,
    },

    middle: {
      type: Boolean,
      required: false,
    },

    lock: {
      type: Boolean,
      required: false,
    },

    center: {
      type: Boolean,
      required: false,
    },

    innerClass: {
      type: String,
      required: false,
      default: '',
    },
  },

  data() {
    return {
      endOfLife: false,
    };
  },

  computed: {
    role() {
      if (this.lock) return 'alertdialog';
      if (['INFO', 'SUCCESS'].includes(this.type)) return 'status';
      return 'alert';
    },

    busy() {
      if (this.type === 'LOADING') return 'true';
      return 'false';
    },

    classes() {
      const classes = [this.innerClass];
      if (this.closable) classes.push('notification-dismissible');
      if (this.middle) classes.push('middle');
      if (this.type === 'ERROR') classes.push('notification-negative');
      if (this.type === 'WARNING') classes.push('notification-warning');
      if (this.type === 'SUCCESS') classes.push('notification-positive');
      if (this.type === 'LOADING') classes.push('loading');
      return classes;
    },

    headingComputed() {
      if (this.heading != null) return this.heading;
      if (this.type === 'SUCCESS') return 'common.headerSuccess';
      if (this.type === 'ERROR') return 'common.headerError';
      if (this.type === 'LOADING') return 'common.loading';
      return null;
    },

    headingId() {
      if (this.heading == null) return null;
      return Math.random().toString(36).substring(2);
    },

    contentComputed() {
      if (this.supplier) return this.supplier();
      if (this.content) return this.$to(this.content);
      return '';
    },

    surroundingComponent() {
      if (this.lock) return require('vue-focus-lock').default;
      return require('./Transparent.vue').default;
    },
  },

  beforeDestroy() {
    this.endOfLife = true;
  },
  methods: {
    statusSymbol,
  },
};
</script>

<style scoped>
.loading h3 {
  padding-top: 4px;
  font-size: 15px;
}
</style>
